import React, { useState } from 'react'

import LoadingSpinnerCentered from 'components/reusable/LoadingSpinnerCentered'
import { Banner, Button } from 'slab'
import { useGetBrokersCoverageAuditLogs } from 'utils/web_api'
import { type GetBrokersCoverageAuditLogsParams } from 'utils/web_api.schemas'

import CoverageAuditLogCard from './CoverageAuditLogCard'

interface CoverageAuditLogCardsViewProps {
  enrollmentId: string
  pageSize?: number
}

const CoverageAuditLogCardsView: React.FC<CoverageAuditLogCardsViewProps> = ({
  enrollmentId,
  pageSize = 10,
}) => {
  const [currentPage, setCurrentPage] = useState(1)

  const params: GetBrokersCoverageAuditLogsParams = {
    page: currentPage,
    items: pageSize,
    'filters[enrollment_id]': enrollmentId,
    'sort[created_at]': 'desc',
  }

  const { data, isLoading, isError } = useGetBrokersCoverageAuditLogs(params)

  if (isLoading) {
    return (
      <div className='py-8'>
        <LoadingSpinnerCentered />
      </div>
    )
  }

  if (isError) {
    return (
      <Banner type='critical' title='Error loading coverage audit logs' />
    )
  }

  if (!data || data?.data?.length === 0) {
    return (
      <div className='text-gray-500 text-sm text-center py-8 bg-gray-50 rounded border border-gray-200'>
        No coverage audit logs found
      </div>
    )
  }

  const { pagination, data: auditLogs } = data
  const hasMultiplePages = pagination?.total_pages && pagination.total_pages > 1

  return (
    <div className='space-y-2 pt-1'>
      {/* Audit log cards */}
      {auditLogs?.map((log) => (
        <CoverageAuditLogCard key={log.id} log={log} />
      ))}

      {/* Pagination */}
      {hasMultiplePages && (
        <div className='flex items-center justify-between pt-2'>
          <div className='pl-2 text-sm text-gray-600'>
            Page {pagination.current_page} of {pagination.total_pages}
            <span className='text-gray-400 ml-2'>
              ({pagination.total_records} total)
            </span>
          </div>
          <div className='flex gap-2'>
            <Button
              type='button'
              variant='tertiary'
              size='small'
              disabled={!pagination.prev_page}
              onClick={() => pagination.prev_page && setCurrentPage(pagination.prev_page)}
            >
              Previous
            </Button>
            <Button
              type='button'
              variant='tertiary'
              size='small'
              disabled={!pagination.next_page}
              onClick={() => pagination.next_page && setCurrentPage(pagination.next_page)}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}

export default CoverageAuditLogCardsView
